import { CartAddEvent, CartUpdateEvent } from '@theme/events';

let adding = false;

export async function addAiruCartItems(items, errorMessage) {
  if (adding) throw new Error(errorMessage);
  adding = true;
  try {
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), el => el.dataset.sectionId))].slice(0, 5);
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
    });
    const result = await response.json().catch(() => { throw new Error(errorMessage); });
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || errorMessage);
    }
    document.dispatchEvent(new CartAddEvent({}, 'airu-cart', {
      source: 'product-form-component',
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      sections: result.sections
    }));
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      await customElements.whenDefined('cart-drawer-component');
      drawer.open();
    }
    return result;
  } catch (cause) {
    try {
      const currentResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (currentResponse.ok) {
        const currentCart = await currentResponse.json();
        document.dispatchEvent(new CartUpdateEvent(currentCart, 'airu-cart', { source: 'airu-cart', itemCount: currentCart.item_count, didError: true }));
      }
    } catch {}
    throw cause instanceof TypeError || cause instanceof SyntaxError ? new Error(errorMessage) : cause;
  } finally {
    adding = false;
  }
}

class AiruCartUpgrades extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol' });
    this.addEventListener('change', () => this.update(), { signal: this.controller.signal });
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-upgrade-add]');
      if (button && this.contains(button)) this.add(button);
    }, { signal: this.controller.signal });
    this.hidden = false;
    this.update();
  }

  disconnectedCallback() { this.controller?.abort(); }

  update() {
    for (const card of this.querySelectorAll('[data-upgrade-card]')) {
      const select = card.querySelector('select');
      if (!select.selectedOptions[0] || select.selectedOptions[0].disabled) {
        const available = Array.from(select.options).find(option => !option.disabled);
        if (available) select.value = available.value;
      }
      const option = select.selectedOptions[0];
      const available = option && !option.disabled;
      const price = Number(option?.dataset.price || 0) - 1000;
      const total = Number(this.dataset.basePrice) + price;
      card.querySelector('[data-upgrade-price]').textContent = available ? this.dataset.extra.replace('[amount]', this.money.format(price / 100)) : this.dataset.unavailable;
      card.querySelector('[data-upgrade-total]').textContent = available ? this.dataset.total.replace('[saving]', this.money.format(10).replace(/\.00$/, '')) : '';
      const shipping = card.querySelector('[data-upgrade-shipping]');
      if (shipping) shipping.hidden = !available || total < 6500;
      card.querySelector('button').disabled = this.busy || !available;
      select.disabled = !!this.busy;
    }
  }

  async add(button) {
    if (this.busy || button.disabled) return;
    const selected = button.closest('[data-upgrade-card]').querySelector('select').selectedOptions[0];
    if (!selected || selected.disabled) return;
    const id = selected.value;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-upgrade-error]');
    error.hidden = true;
    this.update();
    const label = button.textContent;
    button.textContent = this.dataset.adding;
    try {
      const response = await fetch(Theme.routes.cart_url + '.js', {headers:{Accept:'application/json'}, cache:'no-store'});
      if (!response.ok) throw new Error(this.dataset.error);
      const cart = await response.json();
      if (cart.item_count !== 1 || cart.items[0]?.key !== this.dataset.lineKey || cart.total_discount > 0 || cart.total_price !== Number(this.dataset.basePrice)) {
        document.dispatchEvent(new CartUpdateEvent(cart, 'airu-cart', {source:'airu-cart', itemCount:cart.item_count}));
        throw new Error(this.dataset.changed);
      }
      await addAiruCartItems([{id, quantity:1}], this.dataset.error);
      this.hidden = true;
    } catch (cause) {
      error.textContent = cause instanceof TypeError ? this.dataset.error : cause.message;
      error.hidden = false;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      button.textContent = label;
      this.update();
    }
  }
}

if (!customElements.get('airu-cart-upgrades')) customElements.define('airu-cart-upgrades', AiruCartUpgrades);

